/**
 * @module Header
 * @description
 */
import React, { FC } from "react";
import Head from "next/head";

// Tracking & Scripts
import { GTMScript } from "ui/resources/GTM";
import { AdobeSnippet } from "ui/resources/Adobe";
import OneTrust from "ui/resources/OneTrust";
import InvocaScript from "ui/resources/Invoca";
import SchemaMarkup from "ui/resources/SchemaMarkup";
import Fullstory from "ui/resources/Fullstory";
import Rum from "ui/resources/Rum";
import styles from "./index.module.css";

// Components
import StickyNav from "~/core/StickyNav";
import SiteNavigation from "~/core/SiteNavigation";

// Helpers
import { getAsset } from "ui/js/utils/helpers";

import { MonarchSlot } from "@redventures/cohesion-utils-react";

const Header: FC = () => {
  const siteTitle =
    "Business Internet & Telephone Service | Frontier Business Internet";
  const siteDescription =
    "Frontier offers high-speed business internet in addition to telephone services. Stay connected with Frontier as your business internet service provider. Get a quote today!";
  const siteUrl = "/";
  const faviconPath = getAsset("/rv-assets/img/favicons/");

  return (
    <>
      <Head>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />

        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, viewport-fit=cover"
        />
        <meta name="format-detection" content="telephone=no" />
        <link rel="canonical" href="https://business.frontier.com/business" />

        {/* PWA Info */}
        <meta name="application-name" content={siteTitle} />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={siteTitle} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta
          name="msapplication-config"
          content={`${faviconPath}browserconfig.xml`}
        />
        <meta name="msapplication-tap-highlight" content="no" />
        <meta name="theme-color" content="#ffffff" />

        <link
          rel="apple-touch-icon"
          href={`${faviconPath}apple-touch-icon.png`}
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href={`${faviconPath}apple-touch-icon-ipad.png`}
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${faviconPath}apple-touch-icon-iphone-retina.png`}
        />
        <link
          rel="apple-touch-icon"
          sizes="167x167"
          href={`${faviconPath}apple-touch-icon-ipad-retina.png`}
        />

        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${faviconPath}favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${faviconPath}favicon-16x16.png`}
        />
        <link rel="shortcut icon" href={`${faviconPath}favicon.ico`} />

        <link
          rel="manifest"
          href={`${getAsset("/manifest.json")}`}
          type="application/manifest+json"
        />
        <link
          rel="mask-icon"
          href={`${faviconPath}safari-pinned-tab.svg`}
          color="#df4841"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta
          name="twitter:image"
          content={`${faviconPath}android-chrome-192x192.png`}
        />
        <meta name="twitter:creator" content="@FrontierCorp" />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Internet, Phone & TV Service Provider | Frontier.com"
        />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:site_name" content={siteTitle} />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:image" content={`${faviconPath}og-image.jpg`} />
        {/* END PWA Info */}

        {/* Image Preconnects */}
        <link rel="preconnect" href={process.env.imagePath} />
        <SchemaMarkup />
      </Head>

      {/* 3rd Party / External Scripts */}
      {process.env.environment !== "local" && <OneTrust />}
      {process.env.environment !== "local" && <AdobeSnippet />}
      {process.env.environment !== "local" && (
        <>
          <GTMScript accountId="GTM-K8KN3B5" />
          <GTMScript accountId="GTM-KSDV42F8" />
        </>
      )}
      {process.env.environment !== "local" && <InvocaScript />}
      {process.env.environment === "production" && <Fullstory />}
      {process.env.environment !== "local" && <Rum />}

      <header
        id="masthead"
        className={`${styles.siteHeader} site-header`}
        role="banner"
      >
        <a href="#content" className="skip-link screen-reader-text">
          Skip to content
        </a>

        <StickyNav />
        <MonarchSlot slotId="smbLpNavigation">
          <SiteNavigation />
        </MonarchSlot>
      </header>
    </>
  );
};

Header.displayName = "Header";

export default Header;
